import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import React, {
    createContext,
    ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { generatePath, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { type ApiError } from '@/api/client';
import {
    publisherResultImportsApi,
    type PublisherResultImportApprovalResult,
} from '@/api/publisher-result-imports';
import { reportsApi } from '@/api/reports';
import concludeReportImg from '@/assets/images/conclude-report.webp';
import tipDragCustomSectionImg from '@/assets/images/tip-drag-custom-section.webp';
import { ConsolidationReviewModal } from '@/components/modals/ConsolidationReviewModal';
import { MissingCrpModal } from '@/components/modals/MissingCrpModal';
import { SaveTemplateModal } from '@/components/modals/SaveTemplateModal';
import { SubscriptionPlansModal } from '@/components/modals/SubscriptionPlansModal';
import { TemplateSelectionModal } from '@/components/modals/TemplateSelectionModal';
import { findBlockIndexByRealId } from '@/components/preview/block-selection/blockClipboard';
import { resolveBlockScope } from '@/components/preview/block-selection/blockGroups';
import { stripSplitBlockId } from '@/components/preview/pagination/utils';
import { ReportData } from '@/components/preview/types';
import { ReportImportModal } from '@/components/report-import/ReportImportModal';
import ReportCompletion from '@/components/reports/ReportCompletion';
import { EmptyState } from '@/components/system';
import { Skeleton } from '@/components/ui/skeleton';
import { SECTIONS } from '@/components/wizard/constants';
import { WizardStep } from '@/components/wizard/types';
import { ONE_MINUTE } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { useAiGenerationRunner } from '@/hooks/use-ai-generation-runner';
import { useAutoSave } from '@/hooks/use-auto-save';
import { useConfirmDelete } from '@/hooks/use-confirm-delete';
import { ENTITLEMENTS_QUERY_KEY } from '@/hooks/use-entitlements';
import { useFeatureEntitlement } from '@/hooks/use-feature-entitlement';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useLocalStorage } from '@/hooks/use-local-storage';
import { useModal } from '@/hooks/use-modal';
import { READINESS_CHECKLIST_QUERY_KEY } from '@/hooks/use-readiness-checklist';
import {
    useSectionRequirements,
    SECTION_REQUIREMENTS_QUERY_KEY,
} from '@/hooks/use-section-requirements';
import {
    hasTrackedFirstReportCreated,
    markFirstReportCreatedTracked,
    trackFirstReportCreated,
} from '@/lib/analytics';
import { ReportExportTrigger, ReportStepViewVia } from '@/lib/analytics-events';
import {
    EntitlementBlockedError,
    isEntitlementBlockedError,
} from '@/lib/entitlement-blocked-error';
import { getEntitlementBlockedMessage } from '@/lib/entitlements';
import { capture, captureFeatureBlocked, toFailureKind } from '@/lib/posthog';
import {
    applyReportImportPatch,
    reportHasImportableSectionContent,
    type ReportImportPatch,
} from '@/lib/report-imports';
import {
    createCustomSectionId,
    getCustomSectionById,
    getSectionContent,
    getSectionTitle,
    hasCustomReportSections,
    isBuiltInReportSectionId,
    resolveReportSectionOrder,
} from '@/lib/report-sections';
import { isSessionExpired } from '@/lib/session-expiry';
import { createId, createTextBlock } from '@/lib/table-utils';
import { DashboardPage } from '@/pages/constants';
import { ReportTemplatePreviewPage } from '@/pages/constants';
import { ReportTemplate } from '@/types/report';
import { PendingConsolidation, TestBlock } from '@/types/report-payload';
import { canExportReport, getReportLink, isCompletedReport } from '@/utils';
import { downloadBlob, getReportDocxFilename, getReportJsonFilename } from '@/utils/download';
import { CREATE_REPORT_TOUR_STORAGE_KEY } from '../config/tour';
import { STEPS } from '../constants';
import { useConfirmReportCreation } from '../hooks/useConfirmReportCreation';
import { useReportHistory } from '../hooks/useReportHistory';
import { useReportInitialization } from '../hooks/useReportInitialization';
import { ManuallyCompletedSteps, SectionPageMap } from '../types';
import { cloneReportData } from '../utils/cloneReportData';
import { withTestBlockReferences } from '../utils/extractTestReferences';
import { normalizeReportData } from '../utils/normalizeReportData';
import { canSaveReportData } from '../utils/reportSaveGuards';
import {
    CreateReportLocationState,
    TemplatePreviewLocationState,
    buildReportDataFromTemplate,
    clearPendingTemplateState,
} from '../utils/template';
import { PanelScope, PendingBlockAction, WizardContextType } from './types';
import type { ActiveSection } from '@/components/customize/types';
import type {
    PublisherResultImport,
    PublisherResultPatientApprovalStatus,
} from '@/types/publisher-result-import';

const ReportWizardContext = createContext<WizardContextType | undefined>(undefined);

const HISTORY_DEBOUNCE_MS = 400;

const getPendingPublisherResultImportsQueryKey = (reportId?: string | null) =>
    ['publisher-result-imports', 'pending-report', reportId] as const;

const getPublisherResultApprovalStatusesQueryKey = (patientId?: string | null) =>
    ['publisher-result-imports', 'patient-approval-statuses', patientId] as const;

const getApiErrorMessage = (error: unknown) => {
    if (error && typeof error === 'object' && 'response' in error) {
        const response = (
            error as {
                response?: { data?: { description?: string; message?: string } };
            }
        ).response;

        return response?.data?.description || response?.data?.message;
    }

    if (error instanceof Error) return error.message;
    return null;
};

// A 400 on save is the API refusing the payload's shape, and its message is a
// zod path ("body/tests/blocks/0 Unrecognized key…") nobody should read. The
// user can't fix it either: it is front and API disagreeing on the contract.
const isSaveValidationError = (error: unknown) =>
    (error as ApiError | null)?.response?.data?.code === 'FST_ERR_VALIDATION';

const getSaveErrorMessage = (error: unknown) => {
    if (isSaveValidationError(error)) {
        return 'O laudo não foi salvo porque o servidor recusou parte do conteúdo. Se continuar acontecendo, fale com o suporte.';
    }

    return (
        getApiErrorMessage(error) ||
        (error instanceof Error && error.message.trim() ? error.message : 'Erro ao salvar o laudo')
    );
};

// One id for every background save failure, so a payload the API keeps
// refusing replaces its own toast instead of stacking one per minute.
const SAVE_FAILED_TOAST_ID = 'report-save-failed';

type EditableReportPayload = Partial<ReportData> & {
    applied_consolidation_ids?: string[];
};

const RESPONSE_ONLY_REPORT_FIELDS = [
    'id',
    'status',
    'has_anamnesis',
    'created_at',
    'updated_at',
    'content_version',
    'content_updated_at',
    'concluded_at',
    'concluded_by',
    'pending_consolidations',
    'applied_consolidation_ids',
] as const;

const buildEditableReportPayload = (data: ReportData): EditableReportPayload => {
    const payload: Record<string, unknown> = { ...data };

    for (const field of RESPONSE_ONLY_REPORT_FIELDS) {
        delete payload[field];
    }

    return payload as EditableReportPayload;
};

const getReportConcurrencyToken = (data: ReportData) => {
    if (typeof data.content_version === 'number') {
        return { expected_content_version: data.content_version };
    }

    if (data.updated_at) {
        return { expected_updated_at: data.updated_at };
    }

    return {};
};

const mergeServerReportMetadata = (current: ReportData, savedReport: ReportData): ReportData => ({
    ...current,
    id: savedReport.id ?? current.id,
    status: savedReport.status ?? current.status,
    has_anamnesis: savedReport.has_anamnesis ?? current.has_anamnesis,
    created_at: savedReport.created_at ?? current.created_at,
    updated_at: savedReport.updated_at ?? current.updated_at,
    content_version: savedReport.content_version ?? current.content_version,
    content_updated_at: savedReport.content_updated_at ?? current.content_updated_at,
    pending_consolidations: savedReport.pending_consolidations ?? current.pending_consolidations,
});

const preserveReportMetadata = (nextData: ReportData, current: ReportData): ReportData => ({
    ...nextData,
    id: current.id ?? nextData.id,
    status: current.status ?? nextData.status,
    // Not server metadata like the rest, but pinned for the same reason: the
    // patient stops being editable the moment the report exists, and the undo
    // history must not become a side door into what the UI closed. Before the
    // report exists `current.patient` may be empty and undo behaves normally.
    patient: current.patient ?? nextData.patient,
    has_anamnesis: current.has_anamnesis ?? nextData.has_anamnesis,
    created_at: current.created_at ?? nextData.created_at,
    updated_at: current.updated_at ?? nextData.updated_at,
    content_version: current.content_version ?? nextData.content_version,
    content_updated_at: current.content_updated_at ?? nextData.content_updated_at,
    pending_consolidations: current.pending_consolidations ?? nextData.pending_consolidations,
});

const CustomSectionStep = () => (
    <div className="flex flex-col items-center gap-4 p-4 text-sm text-muted-foreground">
        <img
            src={tipDragCustomSectionImg}
            alt=""
            // Intrinsic size, so the panel reserves the box instead of
            // reflowing when the art lands.
            width={1095}
            height={1065}
            decoding="async"
            className="h-40 w-auto object-contain sm:h-52"
        />
        <div className="w-full space-y-2">
            <h3 className="font-semibold text-foreground">Dica:</h3>
            <ul className="list-disc space-y-1 pl-5">
                <li>Edite o título e o conteúdo diretamente no laudo.</li>
                <li>Use a barra lateral esquerda para reposicionar ou remover esta seção.</li>
            </ul>
        </div>
    </div>
);

export const ReportWizardProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { openModal, openWizardModal, closeModal } = useModal();
    const confirmDelete = useConfirmDelete();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const { user } = useAuth();

    const reportId = searchParams.get('id');
    const preSelectedPatient = location.state?.preSelectedPatient;

    const { values: formData, setValues, dirty } = useFormikContext<ReportData>();

    const formDataRef = useRef(formData);
    formDataRef.current = formData;
    const saveQueueRef = useRef<Promise<unknown>>(Promise.resolve());

    const setReportValues = useCallback(
        (nextValues: ReportData, shouldValidate?: boolean) => {
            formDataRef.current = nextValues;
            setValues(nextValues, shouldValidate);
        },
        [setValues],
    );

    const isReadOnly = isCompletedReport(formData);
    const createReportAccess = useFeatureEntitlement('reports.create');
    const customSectionsAccess = useFeatureEntitlement('reports.customSections');
    const coverAccess = useFeatureEntitlement('reports.cover');
    const publisherResultImportsFeature = useFeatureFlag('publisherResultImports.reviewFlow');
    const reportImportFeature = useFeatureFlag('reportImports.docxImport');
    const platformTemplateAccess = useFeatureEntitlement('platformTemplates.use');
    const saveTemplateAccess = useFeatureEntitlement('reportTemplates.custom');

    const { push, undo, redo, canUndo, canRedo } = useReportHistory();

    const historyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pendingHistoryRef = useRef<ReportData | null>(null);

    const flushPendingHistory = useCallback(() => {
        if (historyTimeoutRef.current) {
            clearTimeout(historyTimeoutRef.current);
            historyTimeoutRef.current = null;
        }

        if (pendingHistoryRef.current) {
            push(pendingHistoryRef.current, { alreadyCloned: true });
            pendingHistoryRef.current = null;
        }
    }, [push]);

    const scheduleHistoryCommit = useCallback(
        (currentState: ReportData) => {
            if (isReadOnly) return;

            if (!pendingHistoryRef.current) {
                pendingHistoryRef.current = cloneReportData(currentState);
            }

            if (historyTimeoutRef.current) {
                clearTimeout(historyTimeoutRef.current);
            }

            historyTimeoutRef.current = setTimeout(() => {
                if (pendingHistoryRef.current) {
                    push(pendingHistoryRef.current, { alreadyCloned: true });
                    pendingHistoryRef.current = null;
                }
            }, HISTORY_DEBOUNCE_MS);
        },
        [isReadOnly, push],
    );

    const commitHistory = useCallback(() => {
        if (isReadOnly) return;
        flushPendingHistory();
    }, [flushPendingHistory, isReadOnly]);

    useEffect(() => {
        return () => {
            if (historyTimeoutRef.current) {
                clearTimeout(historyTimeoutRef.current);
            }
        };
    }, []);

    const [isGenerating, setIsGenerating] = useState(false);
    // Why the last save failed, until one succeeds. Autosave and `silentSave`
    // used to only log their errors, so a report the API refused on every tick
    // looked saved until the user reopened it and found it empty.
    const [saveFailure, setSaveFailure] = useState<string | null>(null);
    const backgroundSaveFailureNotifiedRef = useRef(false);
    const [generatingSectionId, setGeneratingSectionId] = useState<string | null>(null);
    const {
        phase: generationPhase,
        begin: beginGeneration,
        end: endGeneration,
        cancel: cancelGeneration,
    } = useAiGenerationRunner();
    const [generatingTestBlockId, setGeneratingTestBlockId] = useState<string | null>(null);
    const [dismissedAiUsageNoticeLevel, setDismissedAiUsageNoticeLevel] = useState<
        'warning' | 'critical' | null
    >(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [scrollTrigger, setScrollTrigger] = useState(0);
    const [panelScope, setPanelScope] = useState<PanelScope | null>(null);
    // Set only by the sidebar's battery list, cleared by any pointerdown in the
    // preview. Kept apart from `panelScope`, which is sticky by design and so
    // would leave a ring on the laudo for the rest of the session.
    const [spotlightAnchorId, setSpotlightAnchorId] = useState<string | null>(null);
    const [pendingEditBlockId, setPendingEditBlockId] = useState<string | null>(null);
    const [pendingBlockAction, setPendingBlockAction] = useState<PendingBlockAction | null>(null);

    /**
     * Why the step is about to change, for `report_wizard_step_viewed`.
     *
     * The event is emitted by an effect in CreateReport(), which only sees that
     * the index moved — not what moved it. The navigation handlers below record
     * the intent here and the effect consumes it.
     *
     * It carries the target step id, not just the reason: clicking the step you
     * are already on leaves an intent behind that never gets consumed, and
     * without the id that stale reason would be attributed to whatever changes
     * the step next.
     */
    const stepViewIntentRef = useRef<{ via: ReportStepViewVia; stepId: string } | null>(null);

    const markStepViewIntent = useCallback((via: ReportStepViewVia, stepId?: string) => {
        stepViewIntentRef.current = stepId ? { via, stepId } : null;
    }, []);

    const consumeStepViewIntent = useCallback((stepId: string): ReportStepViewVia => {
        const pending = stepViewIntentRef.current;
        stepViewIntentRef.current = null;

        return pending?.stepId === stepId ? pending.via : 'programmatic';
    }, []);

    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [sectionPageMap, setSectionPageMap] = useState<SectionPageMap>({});
    const [hasSeenCreateReportTour, setHasSeenCreateReportTour] = useLocalStorage<boolean>(
        CREATE_REPORT_TOUR_STORAGE_KEY,
        false,
    );

    const handleCreateReportTourComplete = useCallback(() => {
        setHasSeenCreateReportTour(true);
    }, [setHasSeenCreateReportTour]);

    const { isLoading, hasPendingTemplatePrompt, openPendingTemplatePrompt } =
        useReportInitialization({
            reportId,
            preSelectedPatient,
            setValues: setReportValues,
        });

    const [showCustomizeModal, setShowCustomizeModalOpen] = useState(false);
    const [customizeSection, setCustomizeSection] = useState<ActiveSection>(null);

    const setShowCustomizeModal = useCallback((show: boolean, section: ActiveSection = null) => {
        setCustomizeSection(show ? section : null);
        setShowCustomizeModalOpen(show);
    }, []);
    const [showCompletionModalState, setShowCompletionModalState] = useState(false);
    const [showWarnings, setShowWarnings] = useState(false);
    const [completionAttempt, setCompletionAttempt] = useState(0);
    const [manuallyCompleted, setManuallyCompleted] = useState<ManuallyCompletedSteps>(
        {} as ManuallyCompletedSteps,
    );
    const [applyingPublisherResultImportId, setApplyingPublisherResultImportId] = useState<
        string | null
    >(null);

    const pendingPublisherResultImportsQueryKey = useMemo(
        () => getPendingPublisherResultImportsQueryKey(reportId),
        [reportId],
    );

    const { data: pendingPublisherResultImports = [] } = useQuery<PublisherResultImport[]>({
        queryKey: pendingPublisherResultImportsQueryKey,
        queryFn: () => publisherResultImportsApi.listPendingForReport(reportId || ''),
        enabled:
            publisherResultImportsFeature.enabled && Boolean(reportId) && !isLoading && !isReadOnly,
    });

    const publisherResultApprovalStatusesQueryKey = useMemo(
        () => getPublisherResultApprovalStatusesQueryKey(formData.patient?.id),
        [formData.patient?.id],
    );

    const { data: publisherResultApprovalStatuses = [] } = useQuery<
        PublisherResultPatientApprovalStatus[]
    >({
        queryKey: publisherResultApprovalStatusesQueryKey,
        queryFn: () =>
            publisherResultImportsApi.getPatientApprovalStatuses([formData.patient?.id || '']),
        enabled:
            Boolean(reportId) &&
            Boolean(formData.patient?.id) &&
            publisherResultImportsFeature.enabled &&
            !isLoading &&
            !isReadOnly,
        refetchInterval: (query) =>
            query.state.data?.some((status) => status.processing_count > 0) ? 2_500 : false,
    });

    const publisherResultApprovalStatus = publisherResultApprovalStatuses[0];
    const pendingPublisherResultApprovalImports = publisherResultImportsFeature.enabled
        ? (publisherResultApprovalStatus?.ready_imports ?? [])
        : [];
    const publisherResultApprovalProcessingCount = publisherResultImportsFeature.enabled
        ? (publisherResultApprovalStatus?.processing_count ?? 0)
        : 0;

    const sidebarSteps = useMemo<WizardStep[]>(() => {
        const stepsById = new Map(STEPS.map((step) => [step.id, step]));
        const patientStep = stepsById.get(SECTIONS.PATIENT);
        const bodySteps = resolveReportSectionOrder(formData).reduce<WizardStep[]>(
            (steps, sectionId) => {
                const builtInStep = stepsById.get(sectionId);

                if (builtInStep) {
                    steps.push({
                        ...builtInStep,
                        title: getSectionTitle(formData, sectionId, builtInStep.title),
                    });
                    return steps;
                }

                const customSection = getCustomSectionById(formData, sectionId);
                if (!customSection) return steps;

                steps.push({
                    id: customSection.id,
                    title: customSection.section_title || 'Nova seção',
                    component: CustomSectionStep,
                    description:
                        'Uma seção livre para o que o seu caso pedir e não se encaixa nas seções padrão do laudo.',
                    required: false,
                    isCustom: true,
                });

                return steps;
            },
            [],
        );

        return patientStep ? [patientStep, ...bodySteps] : bodySteps;
    }, [formData]);

    useEffect(() => {
        if (currentStepIndex < sidebarSteps.length) return;
        setCurrentStepIndex(Math.max(sidebarSteps.length - 1, 0));
    }, [currentStepIndex, sidebarSteps.length]);

    useEffect(() => {
        setDismissedAiUsageNoticeLevel(null);
    }, [reportId]);

    /**
     * The tools panel only drills into blocks, so leaving the step that renders
     * them has to take the panel back to its top level.
     */
    useEffect(() => {
        setPanelScope(null);
    }, [currentStepIndex]);

    /**
     * Pops the panel when the group it is scoped to no longer exists — the user
     * deleted the instrument while the panel was pointed at it. The label is
     * refreshed at the same time so renaming a table updates the breadcrumb.
     */
    useEffect(() => {
        const blocks = formData.tests?.blocks || [];

        setPanelScope((current) => {
            if (!current?.anchorRealId) return current;

            const next = resolveBlockScope(blocks, current.anchorRealId);
            if (!next) return null;

            // The focused block can go while the group survives — deleting the
            // interpretation but not its table. The panel then falls back to
            // the anchor rather than showing actions for something gone.
            const focusedStillThere =
                current.focusedRealId !== null &&
                findBlockIndexByRealId(blocks, stripSplitBlockId(current.focusedRealId)) !== -1;
            const focusedRealId = focusedStillThere ? current.focusedRealId : next.anchorRealId;

            if (
                next.anchorRealId === current.anchorRealId &&
                next.label === current.label &&
                focusedRealId === current.focusedRealId
            ) {
                return current;
            }

            return { anchorRealId: next.anchorRealId, label: next.label, focusedRealId };
        });
    }, [formData.tests?.blocks]);

    const scopePanelToBlock = useCallback(
        (realId: string) => {
            const blocks = formData.tests?.blocks || [];
            const scope = resolveBlockScope(blocks, realId);
            if (!scope) return;

            // `realId` may be a split fragment (`<id>__split__2`); the actions
            // belong to the whole block, so it is normalized here rather than
            // at every call site.
            const focusedRealId = stripSplitBlockId(realId);

            setPanelScope((current) =>
                current?.anchorRealId === scope.anchorRealId &&
                current?.label === scope.label &&
                current?.focusedRealId === focusedRealId
                    ? current
                    : { anchorRealId: scope.anchorRealId, label: scope.label, focusedRealId },
            );
        },
        [formData.tests?.blocks],
    );

    const [appliedConsolidationIds, setAppliedConsolidationIds] = useState<string[]>([]);

    const pendingConsolidations = useMemo<PendingConsolidation[]>(
        () => formData.pending_consolidations ?? [],
        [formData.pending_consolidations],
    );

    const acceptConsolidation = useCallback(
        (consolidation: PendingConsolidation) => {
            const currentFormData = formDataRef.current;
            const currentPending: PendingConsolidation[] =
                currentFormData.pending_consolidations ?? [];
            const incomingBlocks = consolidation.payload?.blocks ?? [];
            if (incomingBlocks.length === 0) return;

            commitHistory();

            const existingBlocks = currentFormData.tests?.blocks ?? [];
            const maxOrder = existingBlocks.reduce((max, b) => Math.max(max, b.order ?? 0), 0);

            const newBlocks: TestBlock[] = [];
            let orderCounter = maxOrder + 1;

            for (const block of incomingBlocks) {
                const cloned = {
                    ...block,
                    id: createId(block.block_type),
                    order: orderCounter++,
                    automatic: false,
                };
                newBlocks.push(cloned as TestBlock);

                if (block.block_type === 'table') {
                    const trailing = createTextBlock();
                    trailing.order = orderCounter++;
                    newBlocks.push(trailing);
                }
            }

            const mergedBlocks = [...existingBlocks, ...newBlocks];

            setReportValues({
                ...currentFormData,
                tests: {
                    ...currentFormData.tests,
                    section_title: currentFormData.tests?.section_title || 'Análise',
                    blocks: mergedBlocks,
                },
                pending_consolidations: currentPending.filter((c) => c.id !== consolidation.id),
            });

            setAppliedConsolidationIds((prev) => [...prev, consolidation.id]);

            toast.success('Resultado adicionado ao laudo');
        },
        [commitHistory, setReportValues],
    );

    const appendPublisherResultBlocks = useCallback(
        (blocks: TestBlock[]) => {
            if (blocks.length === 0) return;

            const currentFormData = formDataRef.current;
            const existingBlocks = currentFormData.tests?.blocks ?? [];
            const existingImportTableIds = new Set(
                existingBlocks
                    .filter((block) => block.block_type === 'table')
                    .map((block) => block.source_import_table_id)
                    .filter(Boolean),
            );
            const skippedTableBlockIds = new Set<string>();

            const maxOrder = existingBlocks.reduce(
                (max, block) => Math.max(max, block.order ?? 0),
                0,
            );
            let orderCounter = maxOrder + 1;

            const newBlocks: TestBlock[] = [];
            for (const block of blocks) {
                if (
                    block.block_type === 'table' &&
                    block.source_import_table_id &&
                    existingImportTableIds.has(block.source_import_table_id)
                ) {
                    if (block.id) skippedTableBlockIds.add(block.id);
                    continue;
                }

                if (
                    block.block_type === 'text' &&
                    block.linked_table_id &&
                    skippedTableBlockIds.has(block.linked_table_id)
                ) {
                    continue;
                }

                newBlocks.push({
                    ...block,
                    order: orderCounter++,
                    automatic: false,
                } as TestBlock);
            }

            if (newBlocks.length === 0) {
                toast.info('Estas tabelas já foram adicionadas ao laudo.');
                return;
            }

            commitHistory();

            setReportValues({
                ...currentFormData,
                tests: {
                    ...currentFormData.tests,
                    section_title: currentFormData.tests?.section_title || 'Análise',
                    blocks: [...existingBlocks, ...newBlocks],
                },
            });
        },
        [commitHistory, setReportValues],
    );

    const openConsolidationReview = useCallback(
        (options?: { skipIntro?: boolean }) => {
            if (pendingConsolidations.length === 0) return;

            openModal({
                title: null,
                size: 'lg',
                bodyClassName: 'overflow-hidden flex flex-col min-h-0 p-0',
                showCloseButton: false,
                content: (
                    <ConsolidationReviewModal
                        consolidations={pendingConsolidations}
                        reportData={formData}
                        onAccept={acceptConsolidation}
                        onClose={closeModal}
                        onDismissPermanently={() => {
                            dismissedConsolidationsRef.current = true;
                        }}
                        skipIntro={options?.skipIntro}
                    />
                ),
            });
        },
        [pendingConsolidations, formData, acceptConsolidation, openModal, closeModal],
    );

    const hasAutoOpenedConsolidationsRef = useRef(false);
    const dismissedConsolidationsRef = useRef(false);

    useEffect(() => {
        if (
            !isLoading &&
            pendingConsolidations.length > 0 &&
            !hasAutoOpenedConsolidationsRef.current &&
            !dismissedConsolidationsRef.current
        ) {
            hasAutoOpenedConsolidationsRef.current = true;
            const timer = setTimeout(() => {
                openConsolidationReview();
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isLoading, pendingConsolidations.length, openConsolidationReview]);

    const canSaveReport = canSaveReportData(formData);

    const { data: sectionRequirements } = useSectionRequirements(reportId);

    const shouldTrackFirstReportCreated = async () => {
        if (!user?.id || hasTrackedFirstReportCreated(user.id)) return false;

        try {
            const response = await reportsApi.list({ page: 1, pageSize: 1 });
            return response.pagination.totalCount === 0;
        } catch (error) {
            console.error('Failed to check first report status:', error);
            return false;
        }
    };

    const saveReportMutation = useMutation({
        mutationFn: async (data: ReportData) => {
            const editablePayload = buildEditableReportPayload(data);

            // A API 403 qualquer payload que carregue uma capa fora do plano, o que
            // travaria o laudo inteiro (inclusive o autosave) para quem baixou de
            // plano. Descartamos a capa em silêncio, como o export já faz.
            if (coverAccess.shouldBlock && editablePayload.customizations?.cover) {
                const customizations = { ...editablePayload.customizations };
                delete customizations.cover;
                editablePayload.customizations = customizations;
            }

            if (hasCustomReportSections(editablePayload) && customSectionsAccess.shouldBlock) {
                throw new EntitlementBlockedError(
                    getEntitlementBlockedMessage(customSectionsAccess.decision),
                );
            }

            const payload = withTestBlockReferences(
                editablePayload as ReportData,
            ) as EditableReportPayload;

            if (appliedConsolidationIds.length > 0) {
                payload.applied_consolidation_ids = appliedConsolidationIds;
            }

            const currentReportId = data.id ?? reportId;
            if (currentReportId) {
                return await reportsApi.update(currentReportId, {
                    ...payload,
                    ...getReportConcurrencyToken(data),
                });
            }
            if (createReportAccess.shouldBlock) {
                throw new EntitlementBlockedError(
                    getEntitlementBlockedMessage(createReportAccess.decision),
                );
            }

            const shouldTrackFirstReport = await shouldTrackFirstReportCreated();
            const createdReport = await reportsApi.create(payload);

            if (shouldTrackFirstReport && !hasTrackedFirstReportCreated(user?.id)) {
                trackFirstReportCreated();
                markFirstReportCreatedTracked(user?.id);
            }

            return createdReport;
        },
        onError: (error) => {
            // The session-expired dialog already owns this case.
            if (isSessionExpired()) return;
            setSaveFailure(getSaveErrorMessage(error));
        },
        onSuccess: (savedReport) => {
            setAppliedConsolidationIds([]);
            setSaveFailure(null);
            backgroundSaveFailureNotifiedRef.current = false;
            toast.dismiss(SAVE_FAILED_TOAST_ID);

            if (savedReport) {
                const nextReport = mergeServerReportMetadata(formDataRef.current, savedReport);
                setReportValues(nextReport, false);
            }

            queryClient.invalidateQueries({ queryKey: ['reports'] });
            queryClient.invalidateQueries({
                queryKey: READINESS_CHECKLIST_QUERY_KEY,
            });
            queryClient.invalidateQueries({
                queryKey: [SECTION_REQUIREMENTS_QUERY_KEY],
            });
            if (!reportId && savedReport.id) {
                navigate(getReportLink(savedReport.id), {
                    state: { preLoadedReport: savedReport },
                });
            }
        },
    });

    const saveCurrentReport = useCallback(async () => {
        const operation = saveQueueRef.current
            .catch(() => undefined)
            .then(() => saveReportMutation.mutateAsync(formDataRef.current));

        saveQueueRef.current = operation.catch(() => undefined);

        return operation;
    }, [saveReportMutation]);

    const showSaveErrorToast = (error: unknown) => {
        const message = getSaveErrorMessage(error);
        if (isEntitlementBlockedError(error)) {
            toast.warning(message);
            return;
        }

        toast.error(message);
    };

    // The guards below read `formDataRef`, not `canSaveReport`: `handleUpdate`
    // writes the ref synchronously, so a caller that sets the patient and saves
    // in the same tick would otherwise be turned away by the previous render's
    // answer.
    // Autosave and `silentSave` run without the user asking, so nothing else
    // would tell them their edits are not on the server. Once per failure
    // streak: the retry every minute must not re-toast or re-track.
    const notifyBackgroundSaveFailure = (error: unknown) => {
        if (isSessionExpired() || backgroundSaveFailureNotifiedRef.current) return;
        backgroundSaveFailureNotifiedRef.current = true;

        capture('report_action_failed', {
            action: 'autosave',
            failure_kind: isEntitlementBlockedError(error) ? 'permission' : toFailureKind(error),
        });
        toast.error('O laudo não foi salvo', {
            id: SAVE_FAILED_TOAST_ID,
            duration: Infinity,
            description:
                'As alterações continuam nesta aba e o salvamento é repetido a cada minuto. Não feche a página enquanto isso.',
            action: { label: 'Tentar de novo', onClick: () => void handleSave() },
        });
    };

    const silentSave = async () => {
        if (!canSaveReportData(formDataRef.current)) return;
        try {
            await saveCurrentReport();
        } catch (error) {
            console.error('Silent save before AI generation failed:', error);
            notifyBackgroundSaveFailure(error);
        }
    };

    const handleSave = async () => {
        if (!canSaveReportData(formDataRef.current)) return;
        try {
            await saveCurrentReport();
            toast.success('Laudo salvo com sucesso');
        } catch (error) {
            // Only the explicit save is tracked, never `silentSave` or the
            // one-minute autosave: a user whose plan blocks the payload fails
            // on every tick, and that would drown the signal in its own noise.
            capture('report_action_failed', {
                action: 'save',
                failure_kind: isEntitlementBlockedError(error)
                    ? 'permission'
                    : toFailureKind(error),
            });
            showSaveErrorToast(error);
        }
    };

    // `onCancel` closes over `handleUpdate`, declared further down. It only ever
    // runs from an effect or a click, long after this module finishes evaluating.
    const { isPendingPatientConfirmation } = useConfirmReportCreation({
        formData,
        reportId,
        isLoading,
        canOpenModal: hasSeenCreateReportTour,
        onConfirm: handleSave,
        onCancel: () => handleUpdate({ patient: undefined }),
    });

    const preparePublisherResultApproval = useCallback(async () => {
        if (!canSaveReportData(formDataRef.current)) return;
        await saveCurrentReport();
    }, [saveCurrentReport]);

    const applyPublisherResultReport = useCallback(
        async (result: PublisherResultImportApprovalResult) => {
            const report = result.report ?? (reportId ? await reportsApi.getById(reportId) : null);
            if (!report) return;

            setReportValues(normalizeReportData(report));
            queryClient.invalidateQueries({ queryKey: ['reports'] });
            queryClient.invalidateQueries({
                queryKey: ['publisher-result-imports'],
            });
            queryClient.invalidateQueries({
                queryKey: READINESS_CHECKLIST_QUERY_KEY,
            });
            queryClient.invalidateQueries({
                queryKey: [SECTION_REQUIREMENTS_QUERY_KEY],
            });
        },
        [queryClient, reportId, setReportValues],
    );

    const handlePublisherResultApproved = useCallback(
        async (result: PublisherResultImportApprovalResult) => {
            if (result.pending_report) {
                queryClient.invalidateQueries({
                    queryKey: ['publisher-result-imports'],
                });
                return;
            }

            await applyPublisherResultReport(result);
        },
        [applyPublisherResultReport, queryClient],
    );

    const applyPendingPublisherResultImport = useCallback(
        async (importId: string) => {
            setApplyingPublisherResultImportId(importId);

            try {
                await preparePublisherResultApproval();
                const result = await publisherResultImportsApi.applyToActiveReport(importId);
                await applyPublisherResultReport(result);
                queryClient.setQueryData<PublisherResultImport[]>(
                    pendingPublisherResultImportsQueryKey,
                    (current) => current?.filter((record) => record.id !== importId) ?? current,
                );

                const skippedCount = result.skipped_duplicate_table_ids.length;
                toast.success(
                    skippedCount > 0
                        ? 'Resultado aplicado ao laudo. Tabelas duplicadas foram ignoradas.'
                        : 'Resultado aplicado ao laudo',
                );
            } catch (error) {
                toast.error(
                    getApiErrorMessage(error) ||
                        'Não foi possível aplicar o resultado importado ao laudo.',
                );
                queryClient.invalidateQueries({
                    queryKey: ['publisher-result-imports'],
                });
            } finally {
                setApplyingPublisherResultImportId(null);
            }
        },
        [
            applyPublisherResultReport,
            pendingPublisherResultImportsQueryKey,
            preparePublisherResultApproval,
            queryClient,
        ],
    );

    // Declared here rather than beside `canSaveReport`, next to its only consumer:
    // the pending confirmation is the whole point of the flag. Without it the
    // one-minute tick would create the report while the user is still looking at
    // the question.
    const canAutoSaveReport =
        !isReadOnly &&
        !isPendingPatientConfirmation &&
        (Boolean(reportId) || (Boolean(formData.patient?.id) && !createReportAccess.shouldBlock));

    useAutoSave({
        data: formData,
        getComparableData: buildEditableReportPayload,
        onSave: async () => {
            const currentReport = formDataRef.current;
            if (reportId || currentReport.patient?.id) {
                try {
                    await saveCurrentReport();
                } catch (error) {
                    notifyBackgroundSaveFailure(error);
                    // Rethrow so `useAutoSave` keeps the data marked unsaved
                    // and tries again on the next tick. Swallowing it here is
                    // what made a refused payload count as saved.
                    throw error;
                }
            }
        },
        interval: ONE_MINUTE,
        enabled: canAutoSaveReport,
    });

    const isSaving = saveReportMutation.isPending;
    const hasSaveFailure = saveFailure !== null;

    // Closing the tab now would throw away edits that only exist here.
    useEffect(() => {
        if (!hasSaveFailure && !isSaving) return;

        const handler = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasSaveFailure, isSaving]);

    const handleUpdate = (
        updates: Partial<ReportData> | ((prev: ReportData) => Partial<ReportData>),
    ) => {
        const resolvedUpdates =
            typeof updates === 'function' ? updates(formDataRef.current) : updates;

        const updateKeys = Object.keys(resolvedUpdates) as Array<keyof ReportData>;
        if (updateKeys.length === 0) return;

        const hasChanges = updateKeys.some((key) => {
            const currentValue = formDataRef.current[key];
            const nextValue = resolvedUpdates[key];

            if (Object.is(currentValue, nextValue)) {
                if (nextValue && typeof nextValue === 'object') return true;
                return false;
            }

            return true;
        });

        if (!hasChanges) return;

        const currentFormData = formDataRef.current;
        const nextValues = {
            ...currentFormData,
            ...resolvedUpdates,
        };

        scheduleHistoryCommit(currentFormData);
        setReportValues(nextValues);
    };

    const handleUndo = () => {
        commitHistory();
        const currentFormData = formDataRef.current;
        const previous = undo(currentFormData);
        if (previous) {
            setReportValues(preserveReportMetadata(previous, currentFormData));
            toast.info('Desfeito');
        }
    };

    const handleRedo = () => {
        commitHistory();
        const currentFormData = formDataRef.current;
        const next = redo(currentFormData);
        if (next) {
            setReportValues(preserveReportMetadata(next, currentFormData));
            toast.info('Refeito');
        }
    };

    const handleBack = async () => {
        if (isReadOnly) {
            navigate(DashboardPage);
            return;
        }

        if (!formData.patient?.id) {
            navigate(DashboardPage);
            return;
        }

        // Leaving is not an answer to "create the report for this patient?" —
        // saving here would create it on the way out.
        if (isPendingPatientConfirmation) {
            navigate(DashboardPage);
            return;
        }

        const createReportDeniedReason = createReportAccess.decision?.reason;
        const isSubscriptionAccessBlocked =
            createReportAccess.isDenied &&
            (createReportDeniedReason === 'trial_not_started' ||
                createReportDeniedReason === 'trial_forfeited' ||
                createReportDeniedReason === 'trial_expired' ||
                createReportDeniedReason === 'subscription_inactive');

        if ((!reportId && createReportAccess.shouldBlock) || isSubscriptionAccessBlocked) {
            navigate(DashboardPage);
            return;
        }

        try {
            await saveCurrentReport();
            toast.success('Laudo salvo com sucesso');
            navigate(DashboardPage);
        } catch (error) {
            showSaveErrorToast(error);
        }
    };

    const handleExportJson = useCallback(() => {
        const snapshot = cloneReportData(formData);
        const blob = new Blob([`${JSON.stringify(snapshot, null, 2)}\n`], {
            type: 'application/json;charset=utf-8',
        });

        downloadBlob(blob, getReportJsonFilename(formData.patient?.full_name, formData.id));
        toast.success('Snapshot do laudo exportado em JSON');
    }, [formData]);

    const exportReport = async (
        report: ReportData,
        trigger: ReportExportTrigger,
        forceProceed = false,
    ) => {
        if (!canExportReport(report)) return;

        const reportId = report.id;
        if (!reportId) return;

        // Pairs with the API's `report_export_blocked`: the difference between
        // the two is how many finished laudos never make it out of the product.
        // `trigger` separates the header button from the post-conclusion modal
        // and from the retry after the CRP prompt — the retry re-enters this
        // function, so without it those exports silently count twice.
        capture('report_export_clicked', { trigger });

        let toastId: string | number | undefined;
        const loadingTimeout = window.setTimeout(() => {
            toastId = toast.info('Iniciando exportação...');
        }, 500);

        try {
            const blob = await reportsApi.export(reportId);

            window.clearTimeout(loadingTimeout);
            if (toastId) toast.dismiss(toastId);

            downloadBlob(blob, getReportDocxFilename(report.patient?.full_name, reportId));
            queryClient.invalidateQueries({ queryKey: ENTITLEMENTS_QUERY_KEY });
            toast.success('Exportação concluída com sucesso');
            closeModal();
        } catch (error: unknown) {
            window.clearTimeout(loadingTimeout);
            if (toastId) toast.dismiss(toastId);

            console.error('Failed to export report', error);
            capture('report_action_failed', {
                action: 'export',
                failure_kind: toFailureKind(error),
            });

            const apiError = error as ApiError;
            if (
                apiError.response?.status === 400 ||
                apiError.status === 400 ||
                apiError.response?.status === 403 ||
                apiError.status === 403
            ) {
                // First check if user has CRP
                if (!user?.crp && !forceProceed) {
                    openModal({
                        title: 'Atualização Cadastral',
                        content: (
                            <MissingCrpModal
                                close={closeModal}
                                onSuccess={() => exportReport(report, 'crp_retry', true)}
                            />
                        ),
                        size: 'md',
                    });
                    return;
                }

                // If user has CRP, assume it is a subscription issue
                openModal({
                    title: 'Atualize seu Plano',
                    content: <SubscriptionPlansModal close={closeModal} />,
                    size: 'lg',
                });
                return;
            }

            toast.error('Erro ao exportar laudo');
            if (forceProceed) closeModal();
        }
    };

    const handleExport = async () => {
        await exportReport(formData, 'header_button');
    };

    const ConcludingSkeleton = () => (
        <div className="space-y-6">
            <div className="text-center">
                <div className="mx-auto mb-3 size-12 rounded-full bg-blue-100" />
                <h2 className="text-lg font-semibold text-foreground">Concluindo laudo...</h2>
                <p className="text-sm text-muted-foreground">
                    Estamos finalizando seu laudo e preparando a exportação.
                </p>
            </div>
            <div className="space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
            </div>
        </div>
    );

    const handleConclude = async () => {
        if (!formData.id) return;

        try {
            if (canSaveReport) {
                await saveCurrentReport();
            }

            const concludedReport = await reportsApi.conclude(formData.id);
            const normalizedConcludedReport = normalizeReportData(concludedReport);

            setReportValues(normalizedConcludedReport);
            queryClient.invalidateQueries({
                queryKey: READINESS_CHECKLIST_QUERY_KEY,
            });
            toast.success('Laudo concluído com sucesso');
            closeModal();
            showCompletionModal(normalizedConcludedReport);
        } catch (error) {
            console.error('Failed to conclude report', error);
            capture('report_action_failed', {
                action: 'conclude',
                failure_kind: toFailureKind(error),
            });
            showSaveErrorToast(error);
            closeModal();
        }
    };

    const hasStepContent = (stepId: string) => {
        if (!isBuiltInReportSectionId(stepId) && stepId !== SECTIONS.PATIENT) {
            return Boolean(getSectionContent(formData, stepId));
        }

        switch (stepId) {
            case SECTIONS.PATIENT:
                return (
                    !!formData.patient?.full_name &&
                    !!formData.patient?.date_of_birth &&
                    !!formData.patient?.gender
                );
            case SECTIONS.DEMAND_DESCRIPTION:
                return !!formData.demand_description?.content;
            case SECTIONS.PROCEDURES:
                return !!formData.procedures?.content;
            case SECTIONS.BEHAVIOR_OBSERVATIONS:
                return !!formData.behavior_observations?.content;
            case SECTIONS.CLINICAL_HISTORY:
                return !!formData.clinical_history?.content;
            case SECTIONS.TESTS:
                return !!formData.tests && Object.keys(formData.tests.blocks || {}).length > 0;
            case SECTIONS.DIAGNOSIS:
                return !!formData.diagnosis?.content;
            case SECTIONS.RECOMMENDATIONS:
                return !!formData.recommendations?.content;
            default:
                return false;
        }
    };

    const isStepComplete = (stepId: string) => {
        return Boolean(manuallyCompleted[stepId] || hasStepContent(stepId));
    };

    const handleSaveTemplate = () => {
        if (saveTemplateAccess.shouldBlock) {
            captureFeatureBlocked('report_templates', saveTemplateAccess.decision);
            toast.warning(getEntitlementBlockedMessage(saveTemplateAccess.decision));
            return;
        }

        if (hasCustomReportSections(formData) && customSectionsAccess.shouldBlock) {
            captureFeatureBlocked('report_templates', customSectionsAccess.decision);
            toast.warning(getEntitlementBlockedMessage(customSectionsAccess.decision));
            return;
        }

        openModal({
            title: 'Salvar como Modelo',
            content: <SaveTemplateModal data={formData} onNext={() => closeModal()} />,
            size: 'md',
        });
    };

    const buildTemplateData = useCallback(
        (template: ReportTemplate) => {
            return buildReportDataFromTemplate(template, {
                patient: formData.patient,
                id: formData.id,
                created_at: formData.created_at,
                updated_at: formData.updated_at,
                status: formData.status || 'in_progress',
            });
        },
        [formData.created_at, formData.id, formData.patient, formData.status, formData.updated_at],
    );

    const applyTemplateToReport = useCallback(
        (template: ReportTemplate) => {
            commitHistory();
            push(formData);
            const nextData = preserveReportMetadata(buildTemplateData(template), formData);
            setReportValues(nextData);
            toast.success('Modelo aplicado com sucesso!');
        },
        [buildTemplateData, commitHistory, formData, push, setReportValues],
    );

    const applyImportedReport = useCallback(
        (patch: ReportImportPatch) => {
            commitHistory();
            push(formData);
            setReportValues(normalizeReportData(applyReportImportPatch(formData, patch)));
            toast.success('Laudo importado. Revise as seções e salve.');
        },
        [commitHistory, formData, push, setReportValues],
    );

    const requestReportImport = useCallback(() => {
        if (isReadOnly) return;
        if (!reportImportFeature.enabled) return;

        openModal({
            title: 'Importar laudo (.docx)',
            description:
                'O texto do documento é distribuído nas seções do editor para você revisar.',
            size: 'lg',
            content: (
                <ReportImportModal
                    hasContent={reportHasImportableSectionContent(formData)}
                    patientId={formData.patient?.id}
                    onClose={closeModal}
                    onApply={(patch) => {
                        closeModal();
                        applyImportedReport(patch);
                    }}
                />
            ),
        });
    }, [
        applyImportedReport,
        closeModal,
        formData,
        isReadOnly,
        openModal,
        reportImportFeature.enabled,
    ]);

    const resolveTemplateForApply = useCallback(async (template: ReportTemplate) => {
        if (template.source !== 'laudopsi') return template;

        return reportsApi.getTemplate(template.id);
    }, []);

    const handleTemplateSelect = useCallback(
        async (template: ReportTemplate) => {
            if (template.source === 'laudopsi' && platformTemplateAccess.shouldBlock) {
                captureFeatureBlocked('report_templates', platformTemplateAccess.decision);
                toast.warning(getEntitlementBlockedMessage(platformTemplateAccess.decision));
                return;
            }

            let templateToApply = template;
            try {
                templateToApply = await resolveTemplateForApply(template);
            } catch (error) {
                console.error('Failed to load full template', error);
                toast.error('Erro ao carregar template');
                return;
            }

            if (dirty) {
                openModal({
                    title: 'Trocar template?',
                    description:
                        'Se você trocar o template agora, todos os dados deste laudo serão substituídos. Deseja continuar?',
                    content: null,
                    variant: 'warning',
                    footer: {
                        acceptText: 'Trocar template',
                        closeText: 'Cancelar',
                        onAccept: (close) => {
                            applyTemplateToReport(templateToApply);
                            close();
                        },
                    },
                });
                return;
            }

            applyTemplateToReport(templateToApply);
            closeModal();
        },
        [
            applyTemplateToReport,
            closeModal,
            dirty,
            openModal,
            platformTemplateAccess.decision,
            platformTemplateAccess.shouldBlock,
            resolveTemplateForApply,
        ],
    );

    const clearLocationPendingTemplate = useCallback(() => {
        navigate(
            {
                pathname: location.pathname,
                search: location.search,
            },
            {
                replace: true,
                state: clearPendingTemplateState(
                    location.state as CreateReportLocationState | undefined,
                ),
            },
        );
    }, [location.pathname, location.search, location.state, navigate]);

    const pendingTemplateId = useMemo(
        () => (location.state as CreateReportLocationState | undefined)?.pendingTemplateId,
        [location.state],
    );

    useEffect(() => {
        if (!reportId || isLoading || !pendingTemplateId) return;

        let isActive = true;

        const applyPendingTemplate = async () => {
            try {
                const templates = await reportsApi.getTemplates();
                const pendingTemplate = templates.find(
                    (template) => template.id === pendingTemplateId,
                );

                if (!isActive) return;

                if (!pendingTemplate) {
                    toast.error('Modelo não encontrado');
                    return;
                }

                await handleTemplateSelect(pendingTemplate);
            } catch (error) {
                if (!isActive) return;
                console.error('Failed to apply pending template', error);
                toast.error('Erro ao aplicar template');
            } finally {
                if (isActive) {
                    clearLocationPendingTemplate();
                }
            }
        };

        applyPendingTemplate();

        return () => {
            isActive = false;
        };
    }, [
        clearLocationPendingTemplate,
        handleTemplateSelect,
        isLoading,
        pendingTemplateId,
        reportId,
    ]);

    const handleOpenTemplates = async () => {
        if (isReadOnly) return;
        try {
            const templates = await reportsApi.getTemplates();

            if (templates.length === 0) {
                openModal({
                    title: 'Meus templates',
                    description: (
                        <EmptyState
                            title="Nenhum template encontrado"
                            description={
                                'Você ainda não tem templates salvos. Salve um modelo para reutilizar em novos laudos.'
                            }
                        />
                    ),
                    content: null,
                    size: 'md',
                });
                return;
            }

            // No title here: the modal renders its own dialog header so the edit
            // and delete branches can replace the title and add a back control.
            openWizardModal({
                content: (
                    <TemplateSelectionModal
                        title="Meus templates"
                        onSkip={closeModal}
                        templates={templates}
                        onPreview={(template: ReportTemplate) => {
                            closeModal();
                            navigate(
                                `/${generatePath(ReportTemplatePreviewPage, {
                                    templateId: template.id,
                                })}`,
                                {
                                    state: {
                                        returnTo: {
                                            pathname: location.pathname,
                                            search: location.search,
                                            state: (location.state || undefined) as
                                                | CreateReportLocationState
                                                | undefined,
                                        },
                                    } as TemplatePreviewLocationState,
                                },
                            );
                        }}
                        onSelect={handleTemplateSelect}
                        showBlankOption={false}
                    />
                ),
                size: 'xl',
            });
        } catch (error) {
            console.error('Failed to load templates', error);
            toast.error('Erro ao carregar seus templates');
        }
    };

    const handleComplete = () => {
        if (isReadOnly) return;
        const incompleteSteps = sidebarSteps.filter(
            (step) => step.required !== false && !isStepComplete(step.id),
        );

        if (incompleteSteps.length > 0) {
            // The API owns `report_completed`; this is the near miss. How many
            // sections short people are when they give up is the difference
            // between "too much work" and "one field they could not fill".
            capture('report_conclude_blocked', {
                incomplete_step_count: incompleteSteps.length,
            });
            setShowWarnings(true);
            setCompletionAttempt((prev) => prev + 1);
            toast.error('Algumas seções ainda não foram concluídas');
            return;
        }

        setShowWarnings(false);

        const missingFinalidadeWarning = formData.finalidade?.trim()
            ? ''
            : ' O campo Finalidade, exigido pela Resolução CFP 06/2019, ainda está vazio.';

        // Concluir é irreversível pela UI: o laudo passa a "somente leitura".
        // Avisamos antes de seguir para o fluxo de conclusão (modelo + conclusão).
        openModal({
            title: 'Concluir laudo?',
            description:
                'Depois de concluído, o laudo ficará somente leitura. Você poderá exportá-lo, mas não editá-lo novamente.' +
                missingFinalidadeWarning,
            content: (
                <img
                    src={concludeReportImg}
                    alt=""
                    // Intrinsic size, so the browser reserves the box instead of
                    // reflowing the modal when the art lands.
                    width={595}
                    height={460}
                    decoding="async"
                    className="mx-auto h-40 w-auto object-contain sm:h-48"
                />
            ),
            footer: {
                closeText: 'Continuar editando',
                acceptText: 'Concluir laudo',
                onAccept: () => {
                    // O provider mantém um único modal: chamar openModal aqui troca o conteúdo
                    // em vez de empilhar, então proceedToConclude abre o próximo modal direto.
                    proceedToConclude();
                },
            },
        });
    };

    const proceedToConclude = () => {
        // Check if report was started from a template
        const usedTemplate = formData.customizations?.template?.name;

        if (!usedTemplate && !saveTemplateAccess.shouldBlock) {
            openModal({
                title: 'Transformar em Modelo?',
                description:
                    'Deseja salvar essa estrutura como um novo modelo para usar em futuros pacientes?',
                content: (
                    <SaveTemplateModal
                        data={formData}
                        onNext={() => {
                            closeModal();
                            // Small delay to allow modal transition
                            setTimeout(() => {
                                showConcludingModal();
                                void handleConclude();
                            }, 100);
                        }}
                    />
                ),
                size: 'md',
            });
            return;
        }

        showConcludingModal();
        void handleConclude();
    };

    const showConcludingModal = () => {
        openModal({
            title: null,
            content: <ConcludingSkeleton />,
            size: 'sm',
        });
    };

    const showCompletionModal = (report: ReportData = formData) => {
        openModal({
            title: null,
            content: <ReportCompletion reportData={report} />,
            footer: {
                acceptText: 'Exportar como DOCX',
                acceptVariant: 'default',
                closeText: 'Voltar ao Dashboard',
                onAccept: async () => {
                    await exportReport(report, 'completion_modal');
                },
                onClose: () => {
                    navigate(DashboardPage);
                },
            },
        });
    };

    const toggleManualComplete = (stepId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setManuallyCompleted((prev) => ({
            ...prev,
            [stepId]: !prev[stepId],
        }));
    };

    // Explicit navigation moves the canvas too: bumping the trigger is what
    // `useHighlightActiveSection` scrolls and flashes on. Every "ir para"
    // surface shares this so the step list and the menus can never drift apart.
    const revealSection = (sectionId?: string) => {
        setScrollTrigger((prev) => prev + 1);
        if (!sectionId) return;

        if (sectionId === SECTIONS.PATIENT) setCurrentPage(1);
        else if (sectionPageMap[sectionId]) setCurrentPage(sectionPageMap[sectionId]);
    };

    const handleStepClick = (index: number) => {
        commitHistory();
        markStepViewIntent('sidebar', sidebarSteps[index]?.id);
        setCurrentStepIndex(index);
        revealSection(sidebarSteps[index]?.id);
    };

    // `via` defaults to 'preview' because the report canvas is by far the
    // busiest caller and passes only the section id; the handful of "ir para"
    // surfaces say so explicitly.
    //
    // It also decides whether the canvas moves. 'preview' means the user just
    // clicked the section inside the laudo — it is already in front of them, so
    // only the sidebars sync. Every other `via` is a deliberate "take me there"
    // and gets the scroll plus the highlight flash.
    const handleSectionClick = (sectionId: string, via: ReportStepViewVia = 'preview') => {
        commitHistory();
        if (sectionId) {
            markStepViewIntent(via, sectionId);
            const stepIndex = sidebarSteps.findIndex((s) => s.id === sectionId);
            if (stepIndex !== -1) setCurrentStepIndex(stepIndex);
            if (via !== 'preview') revealSection(sectionId);
        }
    };

    const handleAddCustomSection = () => {
        if (isReadOnly) return;
        if (customSectionsAccess.shouldBlock) {
            captureFeatureBlocked('report_editor', customSectionsAccess.decision);
            toast.warning(getEntitlementBlockedMessage(customSectionsAccess.decision));
            return;
        }

        const newSectionId = createCustomSectionId();
        markStepViewIntent('new_section', newSectionId);
        let selectedIndex = 1;

        commitHistory();
        handleUpdate((prev) => {
            const currentOrder = resolveReportSectionOrder(prev);
            const nextOrder = [...currentOrder, newSectionId];
            selectedIndex = nextOrder.length;

            return {
                custom_sections: [
                    ...(prev.custom_sections ?? []),
                    {
                        id: newSectionId,
                        section_title: 'Nova seção',
                        content: '',
                    },
                ],
                section_order: nextOrder,
            };
        });

        setCurrentStepIndex(Math.max(selectedIndex, 1));
        setScrollTrigger((prev) => prev + 1);
    };

    // Bridges the section's right-click menu to `CreateCommentDialog`, which
    // lives four levels above it in `CreateReport`. Routing through the context
    // keeps `ReportPreview` and `ReportSection` out of it — the latter memoises
    // on an explicit prop whitelist, so a drilled callback is a stale-prop trap.
    const [commentDraftSectionId, setCommentDraftSectionId] = useState<string | null>(null);

    const requestSectionComment = useCallback((sectionId: string) => {
        setCommentDraftSectionId(sectionId);
    }, []);

    const clearCommentDraft = useCallback(() => {
        setCommentDraftSectionId(null);
    }, []);

    const deleteCustomSection = (sectionId: string) => {
        commitHistory();
        handleUpdate((prev) => ({
            custom_sections: (prev.custom_sections ?? []).filter(
                (section) => section.id !== sectionId,
            ),
            section_order: resolveReportSectionOrder(prev).filter(
                (orderedSectionId) => orderedSectionId !== sectionId,
            ),
        }));
        setManuallyCompleted((prev) => {
            const nextCompleted = { ...prev };
            delete nextCompleted[sectionId];
            return nextCompleted;
        });

        const deletedIndex = sidebarSteps.findIndex((step) => step.id === sectionId);
        if (deletedIndex !== -1 && currentStepIndex >= deletedIndex) {
            setCurrentStepIndex(Math.max(currentStepIndex - 1, 0));
        }
    };

    const handleDeleteCustomSection = (sectionId: string) => {
        if (isReadOnly) return;

        const section = getCustomSectionById(formData, sectionId);
        if (!section) return;

        confirmDelete({
            title: 'Remover seção personalizada?',
            description:
                'Esta seção e seu conteúdo serão removidos do laudo. Esta ação não pode ser desfeita.',
            confirmText: 'Remover seção',
            onConfirm: (close) => {
                deleteCustomSection(sectionId);
                close();
            },
        });
    };

    const dismissAiUsageNotice = useCallback((level: 'warning' | 'critical') => {
        setDismissedAiUsageNoticeLevel(level);
    }, []);

    const value = {
        reportId,
        formData,
        panelScope,
        setPanelScope,
        scopePanelToBlock,
        spotlightAnchorId,
        setSpotlightAnchorId,
        pendingEditBlockId,
        setPendingEditBlockId,
        pendingBlockAction,
        setPendingBlockAction,
        currentStepIndex,
        sidebarSteps,
        scrollTrigger,
        currentPage,
        totalPages,
        sectionPageMap,
        manuallyCompleted,
        showWarnings,
        completionAttempt,
        isLoading,
        isGenerating,
        generatingSectionId,
        generatingTestBlockId,
        generationPhase,
        hasSeenCreateReportTour,
        hasPendingTemplatePrompt,
        dismissedAiUsageNoticeLevel,
        isReadOnly,
        canSaveReport,
        isPendingPatientConfirmation,
        sectionRequirements,
        setCurrentStepIndex,
        setCurrentPage,
        setTotalPages,
        setSectionPageMap,
        setIsGenerating,
        setGeneratingSectionId,
        setGeneratingTestBlockId,
        beginGeneration,
        endGeneration,
        cancelGeneration,
        handleCreateReportTourComplete,
        openPendingTemplatePrompt,
        dismissAiUsageNotice,
        handleUpdate,
        commitHistory,
        handleUndo,
        handleRedo,
        canUndo,
        canRedo,
        silentSave,
        handleSave,
        isSaving,
        saveFailure,
        handleBack,
        handleComplete,
        handleExportJson,
        handleSaveTemplate,
        handleOpenTemplates,
        requestReportImport,
        handleExport,
        isStepComplete,
        toggleManualComplete,
        handleStepClick,
        handleSectionClick,
        consumeStepViewIntent,
        handleAddCustomSection,
        handleDeleteCustomSection,
        commentDraftSectionId,
        requestSectionComment,
        clearCommentDraft,
        showCustomizeModal,
        setShowCustomizeModal,
        customizeSection,
        showCompletionModal: showCompletionModalState,
        setShowCompletionModal: setShowCompletionModalState,
        setShowWarnings,
        pendingConsolidations,
        acceptConsolidation,
        appendPublisherResultBlocks,
        openConsolidationReview,
        pendingPublisherResultImports,
        pendingPublisherResultApprovalImports,
        publisherResultApprovalProcessingCount,
        applyingPublisherResultImportId,
        preparePublisherResultApproval,
        handlePublisherResultApproved,
        applyPendingPublisherResultImport,
    };

    return <ReportWizardContext.Provider value={value}>{children}</ReportWizardContext.Provider>;
};

export const useReportWizard = () => {
    const context = useContext(ReportWizardContext);
    if (!context) {
        throw new Error('useReportWizard must be used within a ReportWizardProvider');
    }
    return context;
};
